import { useState } from "react";

import { AxiosError } from "axios";

import { getUserFacingApiErrorMessage, useBulkAddUpdateFieldCropPractice, useGetFarms, useTranslation } from "utils";

import { FieldListField } from "models";

import { Message as message } from "components";

import { BulkEditCrop } from "pages/app/all-field-view/components/BulkEditCropDrawer.tsx";
import { BulkAddEditCropFormProps, createBulkCropPracticeRequestBody } from "pages/app/all-field-view/utils/BulkDataEntryUtils.ts";
import { type MissingDataForm } from "pages/app/historical-practice-data-table-view/components/AddEditMissingDataDrawer.tsx";
import {
  type BulkDataDrawerMode,
  type BulkDataPointType,
  isItemWritable,
  toPracticeRequest,
} from "pages/app/historical-practice-data-table-view/utils/BulkDataPointUtils";

export type BulkCropDataPointEdit = {
  dataPointType: BulkDataPointType;
  formValues: BulkAddEditCropFormProps;
  touchedFormFields: Partial<BulkAddEditCropFormProps>;
};

// the crops that are writable for the same set of edited data points, and so share one merged set of touched fields
type BulkCropSaveGroup = {
  edits: BulkCropDataPointEdit[];
  bulkEditCrops: BulkEditCrop[];
};

type BulkCropSaveParams = {
  missingDataFormItems: MissingDataForm[];
  fields: FieldListField[];
  // "add" only fills the crops missing a value, so those are the only ones the request may carry
  mode: BulkDataDrawerMode;
};

// Crop-level bulk save (crop name, residue management) — the data points that live on the crop itself rather than
// behind a per-practice endpoint. createBulkCropPracticeRequestBody applies only the fields the farmer actually
// touched on top of each crop's existing values, so editing one data point never rewrites the rest.
export const useBulkCropSave = ({ missingDataFormItems, fields, mode }: BulkCropSaveParams) => {
  const { t } = useTranslation("global");
  const { currentFarm } = useGetFarms();
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const fieldIds = Array.from(new Set(missingDataFormItems.map((item) => item.fieldId)));
  const { mutateAsync: updateBulkCrops } = useBulkAddUpdateFieldCropPractice({ fieldIds, farmId: currentFarm?.id });

  const toBulkEditCrop = (item: MissingDataForm & { crop: NonNullable<MissingDataForm["crop"]> }): BulkEditCrop => ({
    ...item.crop,
    fieldId: item.fieldId,
    isInRedzone: fields.find((field) => field.id === item.fieldId)?.isInRedzone ?? false,
    isPlaceholder: false,
    isMandatory: false,
    isBaseline: false,
    availablePracticeBundles: [],
    fertilizerManagement: undefined,
  });

  const save = async (edits: BulkCropDataPointEdit[]): Promise<boolean> => {
    const editsByCropGroup = missingDataFormItems.reduce((groups, item) => {
      if (!item.crop) return groups;
      const writableEdits = edits.filter((edit) => isItemWritable(item, edit.dataPointType, mode));
      if (writableEdits.length === 0) return groups;

      const groupKey = writableEdits.map((edit) => edit.dataPointType).join();
      const group = groups.get(groupKey) ?? { edits: writableEdits, bulkEditCrops: [] };
      return groups.set(groupKey, { ...group, bulkEditCrops: [...group.bulkEditCrops, toBulkEditCrop({ ...item, crop: item.crop })] });
    }, new Map<string, BulkCropSaveGroup>());
    if (editsByCropGroup.size === 0) return false;
    setIsSaving(true);

    try {
      const requests = Array.from(editsByCropGroup.values()).map(({ edits: groupEdits, bulkEditCrops }) => {
        const touchedFormFields = groupEdits.reduce<Partial<BulkAddEditCropFormProps>>(
          (merged, edit) => ({ ...merged, ...edit.touchedFormFields, crop: { ...merged.crop, ...edit.touchedFormFields.crop } }),
          {}
        );
        // each form also carries untouched crop values of its own, so the touched ones win wherever two forms overlap
        const cropFormData = { ...groupEdits.reduce((merged, edit) => ({ ...merged, ...edit.formValues.crop }), {}), ...touchedFormFields.crop };

        return createBulkCropPracticeRequestBody({ cropFormData, touchedFormFields, bulkEditCrops });
      });

      const errors = requests.flatMap((request) => request.errors);
      if (errors.length > 0) {
        message({ content: errors[0].message, icon: "error" });
        return false;
      }

      const data = requests.flatMap((request) =>
        request.data.map(({ bulkCropPracticesRequest }) => {
          const item = missingDataFormItems.find((candidate) => candidate.key === bulkCropPracticesRequest.crop.farmFieldCropId);
          const existingPractices = [...(item?.tillages ?? []), ...(item?.organicFertilizers ?? []), ...(item?.mineralFertilizers ?? [])];
          return { ...bulkCropPracticesRequest, practices: existingPractices.map(toPracticeRequest) };
        })
      );
      await updateBulkCrops({ data });
      return true;
    } catch (error) {
      const errorMessage = error instanceof AxiosError ? getUserFacingApiErrorMessage(error) : error instanceof Error ? error.message : String(error);
      message({ content: `${errorMessage || t("errors.unknown")}`, icon: "error" });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return { save, isSaving };
};
